import {
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CalendarClock,
  Car,
  CreditCard,
  FileText,
  Landmark,
  LayoutDashboard,
  Package,
  Smartphone,
  Users,
  Wrench,
} from 'lucide-react';
import type { ElementType } from 'react';

export const ROUTES = {
  LOGIN: '/login',
  ROOT: '/',
  DASHBOARD: '/dashboard',
  BOOKINGS: '/booking',
  BOOKING_NEW: '/booking/new',
  BOOKING_DETAIL: '/booking/:bookingId',
  BOOKING_INVOICE: '/booking/:bookingId/invoice',
  CUSTOMERS: '/customers',
  VEHICLES: '/vehicles',
  INVENTORY: '/inventory',
  PURCHASE: '/purchase',
  BILLING: '/billing',
  PAYMENTS: '/payments',
  ACCOUNTS: '/accounts',
  REPORTS: '/reports',
  REPORTS_REVENUE: '/reports/revenue',
  REPORTS_OUTSTANDING_DUES: '/reports/outstanding-dues',
  REPORTS_DAILY_JOB_SHEET: '/reports/daily-job-sheet',
  REPORTS_CUSTOMER_STATEMENT: '/reports/customer-statement',
  REPORTS_TOP_SERVICES: '/reports/top-services',
  REPORTS_GST: '/reports/gst',
  COMPANIES: '/companies',
  USERS: '/users',
  SETTINGS: '/settings',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_UPI: '/settings/upi',
  SETTINGS_BANK: '/settings/bank',
  SETTINGS_PACKAGES: '/settings/packages',
  SETTINGS_JOB_CARD_ITEMS: '/settings/job-card-items',
  SETTINGS_ROLES: '/settings/roles',
  SETTINGS_ROLE_NEW: '/settings/roles/new',
  SETTINGS_ROLE_DETAIL: '/settings/roles/:roleId',
  // ── CRM ────────────────────────────────────────────────────────────────────
  CRM_CUSTOMERS: '/crm/customers',
  CRM_CUSTOMER_PROFILE: '/crm/customers/:customerId',
  CRM_SERVICE_DUE: '/crm/service-due',
  CRM_REMINDERS: '/crm/reminders',
  CRM_OUTSTANDING_DUES: '/crm/outstanding-dues',
  CRM_OUTSTANDING_DUES_CUSTOMER: '/crm/outstanding-dues/:customerId',
  CRM_ANALYTICS: '/crm/analytics',
  CRM_VEHICLES: '/crm/vehicles',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

export function toChildRoutePath(route: RoutePath): string {
  return route.replace(/^\//, '');
}

export interface NavigationModule {
  readonly id: string;
  readonly label: string;
  readonly path: RoutePath;
  readonly icon: ElementType;
  readonly dashboardIcon?: string;
  readonly shortcut?: string;
  readonly groupId: string;
  /**
   * ERP permission code required to see this item.
   * Omit for items always visible (e.g. owner-only or no restriction).
   */
  readonly permission?: string;
}

export interface NavigationGroup {
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavigationModule[];
}

const NAVIGATION_GROUP_DEFINITIONS = [
  { id: 'workshop', label: 'Workshop' },
  { id: 'crm', label: 'CRM' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'finance', label: 'Finance' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'reports', label: 'Reports' },
  { id: 'settings', label: 'Settings' },
  { id: 'admin', label: 'Admin' },
] as const;

export const MODULES: readonly NavigationModule[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    shortcut: 'Alt+1',
    groupId: 'workshop',
    permission: 'workshop.dashboard.read',
  },
  {
    id: 'bookings',
    label: 'Bookings',
    path: ROUTES.BOOKINGS,
    icon: Wrench,
    dashboardIcon: '🔧',
    shortcut: 'Alt+2',
    groupId: 'workshop',
    permission: 'workshop.bookings.read',
  },
  // ── CRM group ────────────────────────────────────────────────────────────
  {
    id: 'crm-customers',
    label: 'Customers',
    path: ROUTES.CRM_CUSTOMERS,
    icon: Users,
    groupId: 'crm',
    permission: 'workshop.crm.read',
  },
  {
    id: 'crm-service-due',
    label: 'Service Due',
    path: ROUTES.CRM_SERVICE_DUE,
    icon: CalendarClock,
    groupId: 'crm',
    permission: 'workshop.crm.read',
  },
  {
    id: 'crm-reminders',
    label: 'Reminders',
    path: ROUTES.CRM_REMINDERS,
    icon: Bell,
    groupId: 'crm',
    permission: 'workshop.crm.read',
  },
  // {
  //   id: 'crm-outstanding-dues',
  //   label: 'Outstanding Dues',
  //   path: ROUTES.CRM_OUTSTANDING_DUES,
  //   icon: CreditCard,
  //   groupId: 'crm',
  //   permission: 'workshop.crm.read',
  // },
  // {
  //   id: 'crm-analytics',
  //   label: 'Analytics',
  //   path: ROUTES.CRM_ANALYTICS,
  //   icon: BarChart3,
  //   groupId: 'crm',
  //   permission: 'workshop.crm.read',
  // },
  {
    id: 'crm-vehicles',
    label: 'Vehicles',
    path: ROUTES.CRM_VEHICLES,
    icon: Car,
    groupId: 'crm',
    permission: 'workshop.crm.read',
  },
  // ── Inventory group ──────────────────────────────────────────────────────
  /* {
    id: 'inventory',
    label: 'Inventory',
    path: ROUTES.INVENTORY,
    icon: Package,
    dashboardIcon: '📦',
    groupId: 'inventory',
  },
  {
    id: 'purchase',
    label: 'Purchase',
    path: ROUTES.PURCHASE,
    icon: ShoppingCart,
    groupId: 'inventory',
  },
  {
    id: 'billing',
    label: 'Billing',
    path: ROUTES.BILLING,
    icon: FileText,
    dashboardIcon: '🧾',
    groupId: 'finance',
  },
  {
    id: 'payments',
    label: 'Payments',
    path: ROUTES.PAYMENTS,
    icon: CreditCard,
    dashboardIcon: '💰',
    groupId: 'finance',
  },
  {
    id: 'accounts',
    label: 'Accounts',
    path: ROUTES.ACCOUNTS,
    icon: BookOpen,
    groupId: 'finance',
  }, */
  {
    id: 'reports-revenue',
    label: 'Revenue Summary',
    path: ROUTES.REPORTS_REVENUE,
    icon: BarChart3,
    groupId: 'reports',
    permission: 'workshop.reports.read',
  },
  {
    id: 'reports-outstanding-dues',
    label: 'Outstanding Dues',
    path: ROUTES.REPORTS_OUTSTANDING_DUES,
    icon: CreditCard,
    groupId: 'reports',
    permission: 'workshop.reports.read',
  },
  {
    id: 'reports-daily-job-sheet',
    label: 'Daily Job Sheet',
    path: ROUTES.REPORTS_DAILY_JOB_SHEET,
    icon: CalendarClock,
    groupId: 'reports',
    permission: 'workshop.reports.read',
  },
  {
    id: 'reports-customer-statement',
    label: 'Customer Statement',
    path: ROUTES.REPORTS_CUSTOMER_STATEMENT,
    icon: Users,
    groupId: 'reports',
    permission: 'workshop.reports.read',
  },
  {
    id: 'reports-top-services',
    label: 'Top Services',
    path: ROUTES.REPORTS_TOP_SERVICES,
    icon: FileText,
    groupId: 'reports',
    permission: 'workshop.reports.read',
  },
  {
    id: 'reports-gst',
    label: 'GST Report',
    path: ROUTES.REPORTS_GST,
    icon: BookOpen,
    groupId: 'reports',
    permission: 'workshop.reports.read',
  },
  {
    id: 'companies',
    label: 'Companies',
    path: ROUTES.COMPANIES,
    icon: Building2,
    dashboardIcon: '🏢',
    groupId: 'admin',
  },
  // {
  //   id: 'users',
  //   label: 'Users',
  //   path: ROUTES.USERS,
  //   icon: UserCog,
  //   groupId: 'admin',
  //   permission: 'workshop.users.read',
  // },
  {
    id: 'settings-upi',
    label: 'UPI Settings',
    path: ROUTES.SETTINGS_UPI,
    icon: Smartphone,
    groupId: 'settings',
    permission: 'workshop.settings.read',
  },
  {
    id: 'settings-bank',
    label: 'Bank Accounts',
    path: ROUTES.SETTINGS_BANK,
    icon: Landmark,
    groupId: 'settings',
    permission: 'workshop.settings.read',
  },
  {
    id: 'settings-packages',
    label: 'Package Configuration',
    path: ROUTES.SETTINGS_PACKAGES,
    icon: Package,
    groupId: 'settings',
    permission: 'workshop.settings.read',
  },
  // {
  //   id: 'settings-job-card-items',
  //   label: 'Job Card Items',
  //   path: ROUTES.SETTINGS_JOB_CARD_ITEMS,
  //   icon: ListChecks,
  //   groupId: 'inventory',
  //   permission: 'workshop.settings.read',
  // },
  // {
  //   id: 'settings-roles',
  //   label: 'Roles & Permissions',
  //   path: ROUTES.SETTINGS_ROLES,
  //   icon: ShieldCheck,
  //   groupId: 'settings',
  //   permission: 'workshop.users.read',
  // },
];

export const NAVIGATION_ITEMS = MODULES;

export const DASHBOARD_MODULES = MODULES.filter(
  (module): module is NavigationModule & { readonly dashboardIcon: string } =>
    typeof module.dashboardIcon === 'string',
);

export const NAVIGATION_GROUPS: readonly NavigationGroup[] = NAVIGATION_GROUP_DEFINITIONS.map(
  (group) => ({
    ...group,
    items: MODULES.filter((module) => module.groupId === group.id),
  }),
);

export function isRouteActive(pathname: string, route: RoutePath): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}
